import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { useQuery, useLazyQuery, useSubscription } from '@apollo/react-hooks';

import { graphqlSchema } from '../../../services/graphql.schema';
import sharedToasterSubject from '../../../services/shared.toasterSubject';
import { numberToCurrencyString } from '../../../services/formatting';
import { buildPaymentCfdiDownloadUrl } from '../../../services/fiscalpopApi';
import { ProfileQL } from '../../../models/Profile';
import { BillingAccountUpdatedPublishEvent } from '../../../models/BillingAccount';
import { OpenSpeiPaymentRecord, PaymentRecordEntry } from '../../../models/Payments';
import './Setup.Pagos.History.scss';
import SelectCard from '../../Forms/SelectCard';

moment.locale('es');

interface SetupPagosHistoryProps {
    currentUser: ProfileQL;
}

function SetupPagosHistory({ currentUser }: SetupPagosHistoryProps) {
    const [openSpeiPayment, setOpenSpeiPayment] = useState<OpenSpeiPaymentRecord>(null);
    const [billingUpdateMessage] = useState(new Subject<string>());

    const [paymentOrders, setPaymentOrders] = useState<PaymentRecordEntry[]>([]);
    const [yearFilter, setYearFilter] = useState<number>(new Date().getFullYear());

    useEffect(() => {
        const subscription = billingUpdateMessage.pipe(debounceTime(200)).subscribe(message => {
            sharedToasterSubject.next({ type: 'confirm', message });
        });
        return () => subscription.unsubscribe();
    }, [billingUpdateMessage]);

    useQuery(graphqlSchema.PAYMENTS.ORDERS.getOpenSpeiPayment, {
        onCompleted: ({ getOpenSpeiPayment }: { getOpenSpeiPayment: OpenSpeiPaymentRecord }) => {
            console.log('Open SPEI payments gotten: ', getOpenSpeiPayment);
            setOpenSpeiPayment(getOpenSpeiPayment);
        }
    });

    useQuery(graphqlSchema.PAYMENTS.ORDERS.getPaymentOrderRecords, {
        onCompleted: ({ getPaymentOrderRecords }: { getPaymentOrderRecords: PaymentRecordEntry[] }) => {
            console.log('Payment orders gotten: ', getPaymentOrderRecords);
            setPaymentOrders(getPaymentOrderRecords);
            const lastYearInOrder = getPaymentOrderRecords[0] ? new Date(getPaymentOrderRecords[0].createdAt).getFullYear() : new Date().getFullYear();
            setYearFilter(lastYearInOrder);
        }
    });

    const [getPaymentOrders] = useLazyQuery(graphqlSchema.PAYMENTS.ORDERS.getPaymentOrderRecords, {
        onCompleted: ({ getPaymentOrderRecords }: { getPaymentOrderRecords: PaymentRecordEntry[] }) => {
            console.log('Payment orders gotten: ', getPaymentOrderRecords);
            setPaymentOrders(getPaymentOrderRecords)
        },
        fetchPolicy: 'network-only'
    });

    const [getOpenSpeiPayment] = useLazyQuery(graphqlSchema.PAYMENTS.ORDERS.getOpenSpeiPayment, {
        onCompleted: ({ getOpenSpeiPayment }: { getOpenSpeiPayment: OpenSpeiPaymentRecord }) => {
            console.log('Open SPEI payments gotten: ', getOpenSpeiPayment);
            setOpenSpeiPayment(getOpenSpeiPayment);
        },
        fetchPolicy: 'network-only'
    });

    useSubscription<{billingAccountUpdated: BillingAccountUpdatedPublishEvent}>(graphqlSchema.FISCALPOP.STRIPE.SUBSCRIPTIONS.billingAccountUpdated, {
        onSubscriptionData: ({ subscriptionData }) => {
            const billingAccountUpdated = subscriptionData.data.billingAccountUpdated;
            console.log(`[Setup Pagos History] <billingAccountUpdated>`, billingAccountUpdated);
            getPaymentOrders();
            getOpenSpeiPayment();
            billingUpdateMessage.next('Se ha actualizado tu cuenta');
        }
    });

    useSubscription(graphqlSchema.FISCALPOP.STRIPE.SUBSCRIPTIONS.speiPaymentRequested, {
        onSubscriptionData: ({ subscriptionData }) => {
            console.log('[AccountSubscriptionManager] speiPaymentRequested', subscriptionData.data?.speiPaymentRequested);
            getOpenSpeiPayment();
        }
    });

    const isClusterAccount = useMemo(() => {
        return !!(currentUser?.billing?.isClusterType && currentUser?.billing?.clusterName);
    }, [currentUser]);

    const paymentOrdersByYear = useMemo(() => {
        return paymentOrders.filter(po => {
            const poYear = new Date(po.createdAt).getFullYear();
            return poYear === yearFilter;
        });
    }, [paymentOrders, yearFilter]);

    const yearFilterOptions = useMemo(() => {
        const yearsSet = new Set<number>();
        paymentOrders.forEach(po => {
            const poYear = new Date(po.createdAt).getFullYear();
            yearsSet.add(poYear);
        });
        const yearsArray = Array.from(yearsSet).sort((a, b) => b - a).slice(0, 5); // Last 5 years MAX
        return yearsArray.map(y => ({ label: y.toString(), value: y }));
    }, [paymentOrders]);

    const getOrderState = (ord: PaymentRecordEntry) => {
        if (ord.isPaid) {
            return <p className="_state good">Pagado</p>;
        }
        else if (ord.isRefunded) {
            return <p className="_state bad">Devuelto</p>;
        }
        else {
            return <p className="_state meh">No pagado</p>;
        }
    }

    const getOrderType = (ord: PaymentRecordEntry) => {
        if (ord.type === 'card') {
            return 'Tarjeta';
        } else if (ord.type === 'spei') {
            return 'SPEI'
        } else if (ord.type === 'MercadoPago') {
            return 'Mercado Pago'
        } else {
            return 'Otro';
        }
    }

    const renderOpenSpeiNotice = () => {
        // Cluster accounts do not receive data
        if (isClusterAccount) return null;
        if (!openSpeiPayment) return null;
        if (currentUser.billing?.hasStripeSubscription) return null; // Subscribed accounts do not have SPEI payments

        return (
            <div className="openSpeiNotice card">
                <div className='noticeText'>
                    <h4>Pago SPEI pendiente</h4>
                    <p className='_onDesktop'>
                        Completa tu transferencia SPEI por <b>$ {numberToCurrencyString(openSpeiPayment.amountCentsRemaining / 100)} MXN</b>. <br />
                        Revisa las instrucciones de pago y <b>recuerda agregar la referencia a tu transferencia bancaria.</b>
                    </p>
                    <p className='_onMobile'>
                        Completa tu transferencia SPEI por <b>$ {numberToCurrencyString(openSpeiPayment.amountCentsRemaining / 100)} MXN</b>. <br />
                        <b>No olvides agregar la referencia a tu pago.</b>
                    </p>
                </div>
                <div className='noticeLink'>
                    <a href={openSpeiPayment.instructionsUrl} target='_blank' rel="noopener noreferrer">
                        Ver instrucciones
                    </a>
                </div>
            </div>
        );
    }

    const renderIsClusterNotice = () => {
        return (
            <div className="movementList card">
                <h4>Historial de pagos</h4>
                <div className="isClusterNotice">
                    <h3>Historial de pagos no disponible para cuentas manejadas externamente</h3>
                </div>
            </div>
        )
    }

    const renderPaymentHistory = () => {
        return paymentOrdersByYear.map((ord, i) => {
            return (
                <div className="paymentEntry" key={i}>
                    <div className='_date'>
                        <p>{moment(ord.createdAt).format('D MMM, YYYY')}</p>
                        <p className="_small">{moment(ord.updatedAt || ord.createdAt).format('hh:mm A')}</p>
                    </div>
                    <p className='_tipoPago'>{getOrderType(ord)}</p>
                    {getOrderState(ord)}
                    <p>$ {numberToCurrencyString(ord.amountCents / 100)}</p>
                    {
                        ord.invoiceUUID && ord.isPaid ?
                            <div className="splitDownlaods">
                                <a rel="noopener noreferrer" href={buildPaymentCfdiDownloadUrl(currentUser.professionalProfile.authToken, ord.invoiceUUID, 'pdf')} download={true}>
                                    PDF
                                </a>
                                <a rel="noopener noreferrer" href={buildPaymentCfdiDownloadUrl(currentUser.professionalProfile.authToken, ord.invoiceUUID, 'xml')} download={true}>
                                    XML
                                </a>
                            </div>
                            :
                            <p className="uuid">
                                {ord.isPaid ? 'No disponible' : ''}
                            </p>
                    }
                </div>
            )
        })
    }

    return (
        <div id="SetupPagosHistory">
            {renderOpenSpeiNotice()}
            <div className="card payments">
                <div className="titleFilter">
                    <h4 className='sectionTitle'>Historial de pagos</h4>
                    <div className="filterControls">
                        <SelectCard
                            label='Año'
                            value={yearFilter}
                            onChange={setYearFilter}
                            options={yearFilterOptions}
                        />
                    </div>
                </div>
                <div className="hisotryPadding">
                    <div className="paymentEntry header">
                        <p>Fecha pago</p>
                        <p className='_tipoPago'>Tipo de pago</p>
                        <p>Estado de Pago</p>
                        <p className='_cantidad'>Cantidad</p>
                        <p className='_download'>Descargar Factura</p>
                    </div>
                    {
                        isClusterAccount ? renderIsClusterNotice() : renderPaymentHistory()
                    }
                </div>
            </div>
        </div>
    );
}

export default SetupPagosHistory;
